import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Label2, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, EnvelopeSimple, Star, ArrowBendUpRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const REQ_TONE = { offen: "amber", uebernommen: "green" };
const TYPE_TONE = { service: "blue", emergency: "red", termin: "gray" };

export default function Portal() {
  const [users, setUsers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [openU, setOpenU] = useState(false);
  const [uform, setUform] = useState({ customer_id: "", email: "", name: "" });

  const load = useCallback(() => {
    Promise.all([api.get("/portal/users"), api.get("/portal/requests"), api.get("/portal/feedback"), api.get("/customers")])
      .then(([u, r, f, c]) => { setUsers(u.data); setRequests(r.data); setFeedback(f.data); setCustomers(c.data); setLoaded(true); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    try { await api.post("/portal/users", { ...uform, grants: ["all"] }); toast.success("Portal-Zugang eingeladen"); setOpenU(false); setUform({ customer_id: "", email: "", name: "" }); load(); }
    catch (e) { toast.error("Fehler", { description: formatApiError(e.response?.data?.detail) }); }
  };
  const activate = async (id) => { await api.post(`/portal/users/${id}/activate`); toast.success("Aktiviert"); load(); };
  const convert = async (id) => { const { data } = await api.post(`/portal/requests/${id}/convert`); toast.success(`In ${data.target.kind} überführt`); load(); };

  if (!loaded) return <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-md" />)}</div>;
  const cname = (id) => customers.find((c) => c.id === id)?.name || id;

  return (
    <div>
      <PageHeader title="Kundenportal" subtitle="Portal-Identität/Freigaben · Anfragen · Feedback" />
      <Tabs defaultValue="requests">
        <TabsList data-testid="portal-tabs">
          <TabsTrigger value="requests">Anfragen ({requests.filter((r) => r.status === "offen").length})</TabsTrigger>
          <TabsTrigger value="users">Portal-Zugänge</TabsTrigger>
          <TabsTrigger value="feedback">Feedback</TabsTrigger>
        </TabsList>

        <TabsContent value="requests">
          <Card className="divide-y divide-border rounded-md border">
            {requests.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">Keine Anfragen.</div>}
            {requests.map((r) => (
              <div key={r.id} data-testid="request-row" className="flex items-center justify-between gap-3 px-5 py-4">
                <div><div className="font-semibold">{r.description}</div><div className="text-xs text-muted-foreground">{r.customer_name || cname(r.customer_id)}</div></div>
                <div className="flex items-center gap-2">
                  <StatusBadge tone={TYPE_TONE[r.type]}>{r.type}</StatusBadge>
                  <StatusBadge tone={REQ_TONE[r.status]}>{r.status}</StatusBadge>
                  {r.status === "offen" && <Button size="sm" variant="outline" className="gap-1.5" data-testid={`convert-${r.id}`} onClick={() => convert(r.id)}><ArrowBendUpRight size={15} />Überführen</Button>}
                </div>
              </div>
            ))}
          </Card>
        </TabsContent>

        <TabsContent value="users">
          <div className="mb-4 flex justify-end">
            <Dialog open={openU} onOpenChange={setOpenU}>
              <DialogTrigger asChild><Button data-testid="invite-portal-button" className="gap-2"><UserPlus size={18} weight="bold" />Zugang einladen</Button></DialogTrigger>
              <DialogContent data-testid="portal-user-dialog"><DialogHeader><DialogTitle className="font-heading">Portal-Zugang einladen</DialogTitle></DialogHeader>
                <DialogDescription>Kunde erhält Zugriff auf seine Anlagenakte.</DialogDescription>
                <div className="space-y-3">
                  <div className="space-y-1.5"><Label2>Kunde</Label2>
                    <Select value={uform.customer_id} onValueChange={(v) => setUform((s) => ({ ...s, customer_id: v }))}>
                      <SelectTrigger data-testid="portal-customer"><SelectValue placeholder="Kunde wählen" /></SelectTrigger>
                      <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div className="space-y-1.5"><Label2>Name</Label2><Input data-testid="portal-name" value={uform.name} onChange={(e) => setUform((s) => ({ ...s, name: e.target.value }))} /></div>
                  <div className="space-y-1.5"><Label2>E-Mail</Label2><Input data-testid="portal-email" value={uform.email} onChange={(e) => setUform((s) => ({ ...s, email: e.target.value }))} /></div>
                </div>
                <DialogFooter><Button data-testid="portal-invite-save" onClick={invite} disabled={!uform.customer_id || !uform.email}>Einladen</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <Card className="divide-y divide-border rounded-md border">
            {users.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">Keine Portal-Zugänge.</div>}
            {users.map((u) => (
              <div key={u.id} data-testid="portal-user-row" className="flex items-center justify-between px-5 py-4">
                <div className="flex items-center gap-3"><EnvelopeSimple size={18} className="text-primary" /><div><div className="font-semibold">{u.name}</div><div className="text-xs text-muted-foreground">{u.email} · {cname(u.customer_id)}</div></div></div>
                <div className="flex items-center gap-2">
                  <StatusBadge tone={u.active ? "green" : "gray"}>{u.active ? "aktiv" : "eingeladen"}</StatusBadge>
                  {!u.active && <Button size="sm" onClick={() => activate(u.id)}>Aktivieren</Button>}
                </div>
              </div>
            ))}
          </Card>
        </TabsContent>

        <TabsContent value="feedback">
          <Card className="divide-y divide-border rounded-md border">
            {feedback.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">Kein Feedback.</div>}
            {feedback.map((f) => (
              <div key={f.id} className="flex items-center justify-between px-5 py-4">
                <div><div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} size={16} weight={i < f.rating ? "fill" : "regular"} className={i < f.rating ? "text-warning" : "text-muted-foreground"} />)}</div><div className="text-sm text-muted-foreground">{f.text}</div></div>
              </div>
            ))}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
